/**
 * IndexedDB Service
 * Handles client-side storage of user data (userId) for avatar persistence
 */

const DB_NAME = 'fashlytic-db';
const DB_VERSION = 2; // Incremented version for potential schema changes
const STORE_NAME = 'user-store';
const USER_KEY = 'avatar-user';

export interface StoredUser {
    userId: string;
    avatarUrl?: string; // Optional (might NOT be in DB if fresh, but useful cache)
    gender?: 'male' | 'female';
    bodyType?: string;
    timestamp: number;
    age?: number;
    height?: number;
    weight?: number;
}

class IndexedDBService {
    private db: IDBDatabase | null = null;
    private dbPromise: Promise<IDBDatabase> | null = null;

    /**
     * Initialize and open the database
     */
    private async openDB(): Promise<IDBDatabase> {
        if (this.db) return this.db;
        if (this.dbPromise) return this.dbPromise;

        this.dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('❌ [IndexedDB] Failed to open database:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(request.result);
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Create user store if it doesn't exist
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                    console.log('✅ [IndexedDB] Created user store');
                }
            };
        });

        return this.dbPromise;
    }

    /**
     * Get stored user data
     */
    async getUser(): Promise<StoredUser | null> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction(STORE_NAME, 'readonly');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.get(USER_KEY);

                request.onerror = () => {
                    console.error('❌ [IndexedDB] Get user error:', request.error);
                    reject(request.error);
                };

                request.onsuccess = () => {
                    const user = request.result as StoredUser | undefined;
                    resolve(user || null);
                };
            });
        } catch (error) {
            console.error('❌ [IndexedDB] Get user failed:', error);
            return null;
        }
    }

    /**
     * Get just the userId
     */
    async getUserId(): Promise<string | null> {
        const user = await this.getUser();
        return user?.userId || null;
    }

    /**
     * Save user data
     */
    async setUser(user: StoredUser): Promise<void> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.put(user, USER_KEY);

                request.onerror = () => {
                    console.error('❌ [IndexedDB] Set user error:', request.error);
                    reject(request.error);
                };

                request.onsuccess = () => {
                    console.log('✅ [IndexedDB] User saved:', user.userId);
                    resolve();
                };
            });
        } catch (error) {
            console.error('❌ [IndexedDB] Set user failed:', error);
            throw error;
        }
    }

    /**
     * Clear user data (logout/reset)
     */
    async clearUser(): Promise<void> {
        try {
            const db = await this.openDB();

            return new Promise((resolve, reject) => {
                const transaction = db.transaction(STORE_NAME, 'readwrite');
                const store = transaction.objectStore(STORE_NAME);
                const request = store.delete(USER_KEY);

                request.onerror = () => {
                    console.error('❌ [IndexedDB] Clear user error:', request.error);
                    reject(request.error);
                };

                request.onsuccess = () => {
                    console.log('✅ [IndexedDB] User cleared');
                    resolve();
                };
            });
        } catch (error) {
            console.error('❌ [IndexedDB] Clear user failed:', error);
            throw error;
        }
    }

    /**
     * Check if user has a saved avatar
     */
    async hasAvatar(): Promise<boolean> {
        const user = await this.getUser();
        return !!(user?.avatarUrl);
    }
}

// Singleton export
export const indexedDBService = new IndexedDBService();
